import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, BarChart3, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface PredictionEvent {
  id: string;
  title: string;
  slug: string;
  probability: number;
  volume: number;
  endDate: string;
  image?: string;
}

const fetchPredictions = async (): Promise<PredictionEvent[]> => {
  const { data, error } = await supabase.functions.invoke("fetch-predictions");
  if (error) throw error;
  return data?.predictions ?? [];
};

const PredictionMarkets = () => {
  const { isPremium } = useAuth();
  const [filter, setFilter] = useState<"all" | "high" | "low">("all");

  const { data: predictions = [], isLoading } = useQuery({
    queryKey: ["predictions"],
    queryFn: fetchPredictions,
    refetchInterval: isPremium ? 120_000 : 300_000,
    staleTime: 60_000,
  });

  const filtered = predictions.filter((p) => {
    if (filter === "high") return p.probability >= 0.6;
    if (filter === "low") return p.probability < 0.4;
    return true;
  });

  const display = isPremium ? filtered : filtered.slice(0, 5);

  return (
    <section id="prediction-markets" className="container mx-auto px-4 py-10">
      <Card className="border-border bg-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="flex items-center gap-2 text-base font-mono uppercase tracking-wider text-foreground">
              <BarChart3 className="h-4 w-4 text-primary" />
              Prediction Markets — Geopolitical
            </CardTitle>
            <div className="flex gap-1">
              {(["all", "high", "low"] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`rounded px-2.5 py-1 text-[10px] font-semibold uppercase transition-colors ${
                    filter === f
                      ? "bg-primary text-primary-foreground"
                      : "bg-secondary text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {f === "high" ? "Likely (>60%)" : f === "low" ? "Unlikely (<40%)" : "All"}
                </button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="grid gap-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-16 animate-pulse rounded bg-secondary" />
              ))}
            </div>
          ) : display.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-8">No prediction markets found.</p>
          ) : (
            <div className="grid gap-2">
              {display.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center gap-3 rounded-lg border border-border bg-secondary/40 p-3 transition-colors hover:bg-secondary/70"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{p.title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[10px] text-muted-foreground">
                        Vol: ${(p.volume / 1_000_000).toFixed(1)}M
                      </span>
                      {p.endDate && (
                        <span className="text-[10px] text-muted-foreground">
                          Ends: {new Date(p.endDate).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1">
                      {p.probability >= 0.5 ? (
                        <TrendingUp className="h-3 w-3 text-[hsl(var(--success))]" />
                      ) : (
                        <TrendingDown className="h-3 w-3 text-[hsl(var(--danger))]" />
                      )}
                      <span
                        className={`text-lg font-bold font-mono ${
                          p.probability >= 0.7
                            ? "text-[hsl(var(--success))]"
                            : p.probability >= 0.4
                            ? "text-[hsl(var(--warning))]"
                            : "text-[hsl(var(--danger))]"
                        }`}
                      >
                        {(p.probability * 100).toFixed(0)}%
                      </span>
                    </div>
                    <Badge
                      variant="outline"
                      className={`text-[9px] ${
                        p.probability >= 0.7
                          ? "border-[hsl(var(--success))]/30 text-[hsl(var(--success))]"
                          : p.probability >= 0.4
                          ? "border-[hsl(var(--warning))]/30 text-[hsl(var(--warning))]"
                          : "border-[hsl(var(--danger))]/30 text-[hsl(var(--danger))]"
                      }`}
                    >
                      {p.probability >= 0.7 ? "LIKELY" : p.probability >= 0.4 ? "UNCERTAIN" : "UNLIKELY"}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!isPremium && predictions.length > 5 && (
            <p className="mt-3 text-center text-xs text-muted-foreground">
              🔒 {predictions.length - 5} more markets available with Premium
            </p>
          )}

          <p className="mt-3 text-[10px] text-muted-foreground text-center">
            Data from Polymarket. Not financial advice.
          </p>
        </CardContent>
      </Card>
    </section>
  );
};

export default PredictionMarkets;
